import React, { useState, useEffect } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import colors from "../constants/colors";

const API_BASE = `${process.env.REACT_APP_API_BASE_URL}/api`;

const LoadingSpinner = () => (
  <span className="flex items-center justify-center">
    <svg
      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
    Signing in...
  </span>
);

const StudentLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [schoolDetails, setSchoolDetails] = useState({
    schoolName: "Gurukul ERP",
    address: "Empowering education through technology.",
  });

  const navigate = useNavigate();

  /* -------------------- FETCH SCHOOL DETAILS -------------------- */
  useEffect(() => {
    axios
      .get(`${API_BASE}/school/getSchool`)
      .then((res) => {
        if (res.data) {
          setSchoolDetails(Array.isArray(res.data) ? res.data[0] : res.data);
        }
      })
      .catch(() => {});
  }, []);

  /* -------------------- FETCH SESSIONS -------------------- */
  useEffect(() => {
    axios
      .get(`${API_BASE}/sessions/allSessions`)
      .then((res) => setSessions(res.data))
      .catch(() => setError("Failed to load academic sessions"));
  }, []);

  /* -------------------- LOGIN HANDLER -------------------- */
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!username || !password || !sessionId) {
      return setError("Please fill all fields");
    }

    setIsLoading(true);

    try {
      const res = await axios.post(`${API_BASE}/auth/login`, {
        username,
        password,
        sessionId,
      });

      const token = res.data.token;
      const decoded = jwtDecode(token);

      // Ensure student role
      if (decoded.role !== "student") {
        setError("Unauthorized access");
        setIsLoading(false);
        return;
      }

      // Save auth + session info
      localStorage.setItem("authToken", token);
      localStorage.setItem("role", "student");
      localStorage.setItem("studentId", decoded.refId);
      localStorage.setItem("currentSessionId", sessionId);

      navigate("/student/dashboard");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid credentials");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col justify-center relative overflow-hidden"
      style={{ background: colors.gradientPrimary }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-400/30 via-indigo-500/20 to-purple-600/40 blur-3xl animate-pulse" />

      <div className="relative z-10 w-full max-w-md mx-auto px-6">
        {/* -------------------- HEADER -------------------- */}
        <div className="text-center mb-10">
<div className="flex justify-center mb-6">
  <img
    src={schoolDetails.logo}
    alt="School Logo"
    className="h-32 w-auto object-contain"
  />
</div>


  <h1 className="text-3xl font-bold" style={{ color: colors.primaryText }}>
    {schoolDetails.schoolName}
  </h1>

  <p className="text-sm text-blue-100 mt-1">
    {schoolDetails.address}
  </p>
</div>


        {/* -------------------- CARD -------------------- */}
        <div className="bg-white/90 backdrop-blur-lg p-8 rounded-2xl shadow-2xl">
          <h2 className="text-xl font-semibold text-center text-blue-800 mb-6">
            Student Login
          </h2>

          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4 text-sm text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <input
              className="w-full mb-4 px-4 py-3 rounded-lg border"
              placeholder="Username / Admission No"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />

            <input
              type="password"
              className="w-full mb-4 px-4 py-3 rounded-lg border"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <select
              className="w-full mb-6 px-4 py-3 rounded-lg border"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
            >
              <option value="">Select Academic Session</option>
              {sessions.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.sessionName}
                </option>
              ))}
            </select>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-lg font-semibold text-white"
              style={{
                background:
                  "linear-gradient(90deg,#4f46e5,#3b82f6,#06b6d4)",
                opacity: isLoading ? 0.8 : 1,
              }}
            >
              {isLoading ? <LoadingSpinner /> : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StudentLogin;
